import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../utils/cn";

const typographyVariants = cva(
  "break-keep overflow-wrap-anywhere transition-colors duration-200",
  {
    variants: {
      variant: {
        // display-* 는 랜딩(마케팅) 전용이라 굵기 700 을 유지한다.
        // 아주 큰 글씨는 600 이면 굵기가 덜 느껴져 밋밋해진다.
        "display-xl":
          "scroll-m-20 text-6xl font-bold tracking-tighter lg:text-8xl leading-[1.1]",
        "display-lg":
          "scroll-m-20 text-5xl font-bold tracking-tighter lg:text-7xl leading-[1.1]",
        "display-md":
          "scroll-m-20 text-4xl font-bold tracking-tight lg:text-5xl leading-tight",
        "display-sm":
          "scroll-m-20 text-3xl font-bold tracking-tight lg:text-4xl leading-tight",
        h1: "scroll-m-20 text-4xl font-semibold tracking-tight lg:text-5xl",
        h2: "scroll-m-20 text-3xl font-semibold tracking-tight first:mt-0",
        h3: "scroll-m-20 text-2xl font-semibold tracking-tight",
        h4: "scroll-m-20 text-xl font-semibold tracking-tight",
        h5: "scroll-m-20 text-lg font-semibold tracking-tight",
        h6: "scroll-m-20 text-base font-semibold tracking-tight",
        "body-lg": "text-lg leading-relaxed",
        body: "text-base leading-7",
        "body-sm": "text-sm leading-6",
        caption: "text-xs leading-relaxed",
        label: "text-2xs leading-none uppercase tracking-wider",
        quote: "mt-6 border-l-2 pl-6 italic",
      },
      color: {
        default: "text-foreground",
        muted: "text-muted-foreground",
        primary: "text-primary",
        secondary: "text-secondary",
        destructive: "text-destructive",
        success: "text-green-600",
        white: "text-white",
        gray: "text-gray-500",
        "gray-light": "text-gray-400",
        "gray-dark": "text-gray-900",
      },
      align: {
        left: "text-left",
        center: "text-center",
        right: "text-right",
        justify: "text-justify",
      },
      weight: {
        thin: "font-thin",
        light: "font-light",
        normal: "font-normal",
        medium: "font-medium",
        semibold: "font-semibold",
        bold: "font-bold",
        extrabold: "font-extrabold",
        black: "font-black",
      },
      italic: {
        true: "italic",
      },
      underline: {
        true: "underline underline-offset-4",
      },
      uppercase: {
        true: "uppercase",
      },
      truncate: {
        true: "truncate",
      },
    },
    defaultVariants: {
      variant: "body-lg",
      color: "default",
    },
  },
);

type TypographyVariantProps = VariantProps<typeof typographyVariants>;

export interface TypographyProps<T extends React.ElementType = "p"> {
  /** 렌더링될 HTML 태그 또는 컴포넌트입니다. */
  as?: T;
  /** 텍스트의 시각적 스타일을 결정합니다. */
  variant?: TypographyVariantProps["variant"];
  /** 텍스트의 색상을 결정합니다. */
  color?: TypographyVariantProps["color"];
  /** 텍스트 정렬 방향을 설정합니다. */
  align?: TypographyVariantProps["align"];
  /** 폰트의 굵기를 강제로 지정할 때 사용합니다. (기본값은 variant 설정을 따름) */
  weight?: TypographyVariantProps["weight"];
  /** 기울임꼴 여부 */
  italic?: boolean;
  /** 밑줄 여부 */
  underline?: boolean;
  /** 대문자 변환 여부 */
  uppercase?: boolean;
  /** 한 줄 말줄임 여부 */
  truncate?: boolean;
  /** 여러 줄 말줄임 개수 (1-6) */
  lineClamp?: 1 | 2 | 3 | 4 | 5 | 6;
  /** 추가적인 Tailwind 클래스 (레이아웃 전용: 마진, 패딩 등) */
  className?: string;
  /** 텍스트 내용 */
  children?: React.ReactNode;
  /** DOM 엘리먼트에 직접 접근하기 위한 ref입니다. */
  ref?: React.Ref<any>;
}

function Typography<T extends React.ElementType = "p">({
  as,
  variant,
  color,
  align,
  weight,
  italic,
  underline,
  uppercase,
  truncate,
  lineClamp,
  className,
  children,
  ref,
  ...props
}: TypographyProps<T> &
  Omit<React.ComponentPropsWithoutRef<T>, keyof TypographyProps<any>>) {
  const Comp = as ?? "p";

  // line-clamp 클래스 동적 생성
  const lineClampClass = lineClamp ? `line-clamp-${lineClamp}` : undefined;

  return (
    <Comp
      ref={ref}
      className={cn(
        typographyVariants({
          variant,
          color,
          align,
          weight,
          italic,
          underline,
          uppercase,
          truncate,
          className,
        }),
        lineClampClass,
      )}
      {...props}
    >
      {children}
    </Comp>
  );
}

export { Typography, typographyVariants };
